import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import ActionButton from '../components/ui/ActionButton';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/close`;
const FINANCIAL_API = `${API_V1.replace('/v1', '')}/financial`;
// M26B: continuous-close primary view. Legacy SAP-FCC checklist + gantt removed.
const TABS = ['Dashboard', 'Exceptions', 'Period Lock', 'Audit Trail'];

export default function CloseManagement() {
  const { entity } = useGlobalEntity();
  const [tab, setTab] = useState('Dashboard');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Dashboard
  const [dashboard, setDashboard] = useState<any>(null);
  const [currentPeriod, setCurrentPeriod] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Checklist
  const [checklist, setChecklist] = useState<any>(null);
  const [checklistPeriod, setChecklistPeriod] = useState(currentPeriod);
  const [showCreateChecklist, setShowCreateChecklist] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);

  // Period controls
  const [periods, setPeriods] = useState<any[]>([]);
  const [pcYear, setPcYear] = useState(new Date().getFullYear());

  // Closing entries
  const [closingPreview, setClosingPreview] = useState<any>(null);
  const [closingHistory, setClosingHistory] = useState<any[]>([]);
  const [closingPeriod, setClosingPeriod] = useState(currentPeriod);
  const [showPreview, setShowPreview] = useState(false);

  // M24A.4: CFO override reopen
  const [cfoReopenTarget, setCfoReopenTarget] = useState<any>(null);
  const [cfoReopenReason, setCfoReopenReason] = useState('');

  // M24B: pre-close engines
  const [engineReport, setEngineReport] = useState<any>(null);
  const [engineRunning, setEngineRunning] = useState<string | null>(null);

  // M26A: continuous close status
  const [contClose, setContClose] = useState<any>(null);
  const [contCloseRunning, setContCloseRunning] = useState(false);
  const CC_API = `${API_V1}/continuous-close`;

  // M26B: exceptions inbox + audit trail
  const [exceptions, setExceptions] = useState<any[]>([]);
  const [auditTrail, setAuditTrail] = useState<any[]>([]);
  const [auditSourceFilter, setAuditSourceFilter] = useState('');
  const [auditSourceTypes, setAuditSourceTypes] = useState<string[]>([]);
  const [showLegacy, setShowLegacy] = useState(false);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok }); setTimeout(() => setToast(null), 4000);
  };

  const loadDashboard = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/dashboard?period=${currentPeriod}`, entity));
      if (res.ok) setDashboard(await res.json());
    } catch (e) { console.error(e); }
  }, [currentPeriod, entity]);

  const loadContClose = useCallback(async () => {
    try {
      const res = await authFetch(`${CC_API}/status`);
      if (res.ok) setContClose(await res.json());
    } catch (e) { console.error(e); }
  }, [CC_API]);

  const loadExceptions = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/exceptions?period=${currentPeriod}`);
      if (res.ok) setExceptions(await res.json());
    } catch (e) { console.error(e); }
  }, [currentPeriod]);

  const loadAuditTrail = useCallback(async () => {
    try {
      const url = `${API}/audit-trail?period=${currentPeriod}${auditSourceFilter ? `&source_type=${auditSourceFilter}` : ''}`;
      const res = await authFetch(withEntityParam(url, entity));
      if (res.ok) setAuditTrail(await res.json());
      if (auditSourceTypes.length === 0) {
        const stRes = await authFetch(`${API}/audit-trail/source-types`);
        if (stRes.ok) {
          const d = await stRes.json();
          setAuditSourceTypes(d.source_types || []);
        }
      }
    } catch (e) { console.error(e); }
  }, [currentPeriod, entity, auditSourceFilter, auditSourceTypes.length]);

  const runContClose = async () => {
    setContCloseRunning(true);
    try {
      const res = await authFetch(`${CC_API}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Continuous close ran: ${d.status} — books through ${d.books_closed_through || 'n/a'} (${d.je_count} JEs)`);
        loadContClose();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(e.detail || 'Continuous close failed', false);
      }
    } catch (e) {
      showToast('Error', false);
    } finally {
      setContCloseRunning(false);
    }
  };

  const loadChecklist = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/checklists/${checklistPeriod}`, entity));
      if (res.ok) setChecklist(await res.json());
    } catch (e) { console.error(e); }
  }, [checklistPeriod, entity]);

  const loadTemplates = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/templates`);
      if (res.ok) setTemplates(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  const loadPeriods = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/periods?fiscal_year=${pcYear}`, entity));
      if (res.ok) setPeriods(await res.json());
    } catch (e) { console.error(e); }
  }, [pcYear, entity]);

  const loadClosingHistory = useCallback(async () => {
    try {
      const res = await authFetch(withEntityParam(`${API}/closing-entries/history`, entity));
      if (res.ok) setClosingHistory(await res.json());
    } catch (e) { console.error(e); }
  }, [entity]);

  useEffect(() => { loadDashboard(); loadContClose(); }, [loadDashboard, loadContClose]);
  useEffect(() => { if (tab === 'Exceptions') loadExceptions(); }, [tab, loadExceptions]);
  useEffect(() => { if (tab === 'Period Lock') loadPeriods(); }, [tab, loadPeriods]);
  useEffect(() => { if (tab === 'Audit Trail') loadAuditTrail(); }, [tab, loadAuditTrail]);
  useEffect(() => { if (tab === 'Audit Trail') loadAuditTrail(); }, [auditSourceFilter, tab, loadAuditTrail]);
  // Legacy loaders kept for the "Show legacy" admin toggle
  useEffect(() => { if (showLegacy) { loadChecklist(); loadTemplates(); loadClosingHistory(); } }, [showLegacy, loadChecklist, loadTemplates, loadClosingHistory]);

  const handleCreateChecklist = async (templateId?: number) => {
    try {
      const body: any = { period: checklistPeriod };
      if (templateId) body.template_id = templateId;
      const res = await authFetch(`${API}/checklists`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) { showToast(`Checklist created for ${checklistPeriod}`); setShowCreateChecklist(false); loadChecklist(); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handleTaskAction = async (taskId: number, action: string, body: any = {}) => {
    try {
      const method = action === 'sign-off' || action === 'assign' ? 'POST' : 'PUT';
      const url = action === 'sign-off' || action === 'assign'
        ? `${API}/tasks/${taskId}/${action}`
        : `${API}/tasks/${taskId}`;
      const res = await authFetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      if (res.ok) { showToast(`Task updated`); loadChecklist(); loadDashboard(); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handlePeriodAction = async (entityCode: string, year: number, month: number, action: string) => {
    try {
      const res = await authFetch(`${FINANCIAL_API}/periods/${entityCode}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, month }),
      });
      if (res.ok) { showToast(`Period ${action} successful`); loadPeriods(); loadDashboard(); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const runEngine = async (engine: string) => {
    setEngineRunning(engine);
    try {
      const periodEnd = `${currentPeriod}-28`;
      const url = `${API}/engines/${engine}`;
      const res = await authFetch(url, {
        method: engine === 'readiness-check' ? 'GET' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: engine === 'readiness-check' ? undefined : JSON.stringify({ period_end: periodEnd, entity_code: entity }),
      });
      if (res.ok) {
        const data = await res.json();
        setEngineReport({ engine, data });
        showToast(`${engine} complete`);
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(e.detail || `${engine} failed`, false);
      }
    } catch (e) {
      showToast('Engine error', false);
    } finally {
      setEngineRunning(null);
    }
  };

  const handleCfoReopen = async () => {
    if (!cfoReopenTarget) return;
    if (cfoReopenReason.trim().length < 10) {
      showToast('Reason must be at least 10 characters', false);
      return;
    }
    try {
      const res = await authFetch(`${FINANCIAL_API}/periods/${cfoReopenTarget.entity_code}/cfo-reopen`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          year: cfoReopenTarget.fiscal_year,
          month: cfoReopenTarget.fiscal_month,
          reason: cfoReopenReason.trim(),
        }),
      });
      if (res.ok) {
        showToast(`Period ${cfoReopenTarget.fiscal_year}-${String(cfoReopenTarget.fiscal_month).padStart(2, '0')} reopened by CFO override`);
        setCfoReopenTarget(null);
        setCfoReopenReason('');
        loadPeriods();
        loadDashboard();
      } else {
        const e = await res.json().catch(() => ({}));
        showToast(e.detail || 'CFO reopen failed', false);
      }
    } catch (e) {
      showToast('Error', false);
    }
  };

  const handlePreviewClosing = async () => {
    try {
      const res = await authFetch(`${API}/closing-entries/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: closingPeriod }),
      });
      if (res.ok) { setClosingPreview(await res.json()); setShowPreview(true); }
      else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Preview failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const handlePostClosing = async () => {
    try {
      const res = await authFetch(`${API}/closing-entries/post`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ period: closingPeriod }),
      });
      if (res.ok) {
        const d = await res.json();
        showToast(`Closing JE posted: ${d.je_number} — Net income ${formatAmount(d.net_income)} to RE (${d.re_account})`);
        setShowPreview(false); loadClosingHistory(); loadDashboard();
      } else { const e = await res.json().catch(() => ({})); showToast(e.detail || 'Post failed', false); }
    } catch (e) { showToast('Error', false); }
  };

  const lbl = { fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, marginBottom: 2 };
  const thStyle = { padding: '6px 10px', fontSize: 10, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase' as const, textAlign: 'left' as const, borderBottom: '2px solid var(--border-subtle)' };
  const tdStyle = { padding: '6px 10px', fontSize: 13, borderBottom: '1px solid var(--border-subtle)' };
  const inputStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid var(--border-subtle)', fontSize: 13 };

  const readiness = dashboard?.readiness;
  const cl = dashboard?.checklist;

  return (
    <PageLayout title="Close Management" subtitle="Period-end close checklists, controls, and closing entries">
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, padding: '10px 20px',
          borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, maxWidth: 500,
          background: toast.ok ? '#16a34a' : '#ef4444' }}>{toast.msg}</div>
      )}

      {/* M26A: Continuous Close hero banner -- the AI-native replacement
          for the legacy 23-task checklist. Books are closed nightly by
          the orchestrator; this banner is the operator's "is everything OK"
          glance. */}
      {contClose && (
        <div style={{
          marginBottom: 16, padding: 20, borderRadius: 12,
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
          color: '#fff', display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 20, alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Books Closed Through</div>
            <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>
              {contClose.books_closed_through || '—'}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
              Continuous close · {contClose.trigger || 'SCHEDULED'} · last run {contClose.last_run_at ? new Date(contClose.last_run_at).toLocaleString() : 'never'}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Confidence</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4,
              color: contClose.confidence_pct >= 90 ? '#22c55e' : contClose.confidence_pct >= 70 ? '#f59e0b' : '#ef4444' }}>
              {contClose.confidence_pct ? `${Math.round(contClose.confidence_pct)}%` : '—'}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Engine-weighted</div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last-Run Status</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4,
              color: contClose.last_run_status === 'SUCCESS' ? '#22c55e' :
                     contClose.last_run_status === 'PARTIAL' ? '#f59e0b' :
                     contClose.last_run_status === 'FAILED' ? '#ef4444' : '#94a3b8' }}>
              {contClose.last_run_status || 'NEVER RUN'}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>
              {contClose.je_count || 0} JEs posted
            </div>
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Exceptions</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4,
              color: (contClose.exceptions_count || 0) === 0 ? '#22c55e' : '#ef4444' }}>
              {contClose.exceptions_count || 0}
            </div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Need human review</div>
          </div>
          <div>
            <ActionButton label={contCloseRunning ? 'Running...' : 'Run Now'}
              onClick={runContClose} variant="success" />
          </div>
        </div>
      )}

      {/* Period Selector (for the legacy checklist / closing entries tabs) */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <div style={lbl}>Reporting Period</div>
        <input type="month" value={currentPeriod} onChange={e => setCurrentPeriod(e.target.value)}
          style={inputStyle} title="Reporting Period" aria-label="Reporting Period" placeholder="YYYY-MM" />
        <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>
          (period-end is a 1-click lock — substance happens nightly above)
        </span>
      </div>

      <div style={{ marginTop: 16 }}>
        <TabBar tabs={TABS.map(t => ({ key: t, label: t }))} active={tab} onChange={v => setTab(v)} />
      </div>

      {/* ═══ TAB: Dashboard ═══ */}
      {tab === 'Dashboard' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
          {/* M26A: Continuous Close Engine Health */}
          {contClose && contClose.engines && contClose.engines.length > 0 && (
            <div style={{ padding: 16, borderRadius: 8, background: 'var(--bg-card)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>
                Continuous Close Engine Health
                <span style={{ fontSize: 10, color: 'var(--text-hint)', marginLeft: 8, fontWeight: 400 }}>
                  Last run · {contClose.last_run_at ? new Date(contClose.last_run_at).toLocaleString() : 'n/a'}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {contClose.engines.map((entry: any, i: number) => {
                  const [name, info] = entry;
                  const status = info?.status || 'UNKNOWN';
                  const color = status === 'SUCCESS' ? '#22c55e'
                    : status === 'FAILED' ? '#ef4444'
                    : status === 'SKIPPED' ? '#94a3b8' : '#f59e0b';
                  return (
                    <div key={i} style={{ padding: 10, borderRadius: 6, border: '1px solid var(--border-subtle)',
                      borderLeft: `3px solid ${color}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>{String(name).replace(/_/g, ' ')}</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color }}>{status}</div>
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-hint)', marginTop: 4 }}>
                        {info?.je_count || 0} JEs · {info?.exceptions_count || 0} exc · {info?.duration_ms || 0}ms
                      </div>
                      {info?.error && (
                        <div style={{ fontSize: 10, color: '#ef4444', marginTop: 4, fontFamily: 'monospace' }}>
                          {String(info.error).slice(0, 120)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* M26A: Exception inbox -- only the things auto-close couldn't handle.
              Replaces the 23-task ritual checklist as the operator's daily view. */}
          {contClose && (contClose.exceptions_count || 0) === 0 && contClose.last_run_status === 'SUCCESS' && (
            <div style={{ padding: 16, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#166534' }}>
                ✓ No exceptions — auto-close handled everything
              </div>
              <div style={{ fontSize: 11, color: '#14532d', marginTop: 4 }}>
                Books closed through {contClose.books_closed_through}. The legacy 23-task checklist below is shown
                only as a fallback view of what the orchestrator covers.
              </div>
            </div>
          )}

          {/* M26B.2: legacy SAP-FCC panels demoted behind admin toggle.
              Continuous close above is the canonical view; these are for
              transition / audit comparison only. */}
          <div>
            <button
              type="button"
              onClick={() => setShowLegacy(!showLegacy)}
              style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--text-hint)', cursor: 'pointer', padding: '4px 0' }}
            >
              {showLegacy ? '▼' : '▶'} Legacy SAP-FCC view (admin / audit only)
            </button>
          </div>

          {/* Readiness components (legacy view — kept for transition) */}
          {showLegacy && readiness && (
          <div style={{ padding: 16, borderRadius: 8, background: 'var(--bg-card)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, opacity: 0.7 }}>Legacy Readiness Components (M24)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {Object.entries(readiness.components || {}).map(([key, comp]: [string, any]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 180, fontSize: 12, fontWeight: 600 }}>{key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</div>
                  <div style={{ flex: 1, height: 16, background: 'var(--bg-elevated)', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 8, width: `${comp.score}%`,
                      background: comp.score >= 80 ? '#16a34a' : comp.score >= 50 ? '#f59e0b' : '#ef4444',
                    }} />
                  </div>
                  <div style={{ width: 50, fontSize: 12, fontWeight: 600, textAlign: 'right' }}>{comp.score}%</div>
                  <div style={{ width: 30, fontSize: 10, color: 'var(--text-hint)' }}>({comp.weight}%)</div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* M24B legacy: Pre-close engines kept as admin-override tooling.
              Continuous close above runs these nightly without operator
              involvement; this panel is for ad-hoc re-runs / audit. */}
          {showLegacy && (
          <div style={{ padding: 16, borderRadius: 8, background: 'var(--bg-card)', opacity: 0.7 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Manual Engine Override (M24 — admin)</div>
              <ActionButton label="Run All Engines" size="sm"
                onClick={() => runEngine('run-pre-close-suite')} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div style={{ padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>FX Revaluation</div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', marginBottom: 6 }}>SAP FAGL_FC_VAL — restate FC monetary items</div>
                <ActionButton label={engineRunning === 'fx-revaluation' ? 'Running...' : 'Run'} size="sm"
                  onClick={() => runEngine('fx-revaluation')} />
              </div>
              <div style={{ padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>GR/IR Aging Report</div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', marginBottom: 6 }}>SAP F.13 — unmatched GRNI by age</div>
                <ActionButton label={engineRunning === 'grni-aging-report' ? 'Running...' : 'Run'} size="sm"
                  onClick={() => runEngine('grni-aging-report')} />
              </div>
              <div style={{ padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>AR/AP Aging Snapshot</div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', marginBottom: 6 }}>SAP FAGLF101 preview (no JE)</div>
                <ActionButton label={engineRunning === 'aging-snapshot' ? 'Running...' : 'Snapshot'} size="sm"
                  onClick={() => runEngine('aging-snapshot')} />
              </div>
              <div style={{ padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>AR/AP Regrouping</div>
                <div style={{ fontSize: 10, color: 'var(--text-hint)', marginBottom: 6 }}>SAP FAGLF101 — post reclass JEs (auto-reverse)</div>
                <ActionButton label={engineRunning === 'ar-ap-regrouping' ? 'Running...' : 'Post JEs'} size="sm" variant="success"
                  onClick={() => runEngine('ar-ap-regrouping')} />
              </div>
            </div>
            {engineReport && (
              <div style={{ marginTop: 12, padding: 10, background: 'var(--bg-elevated)', borderRadius: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 6 }}>{engineReport.engine} result</div>
                <pre style={{ fontSize: 10, fontFamily: 'monospace', whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto', margin: 0 }}>
                  {JSON.stringify(engineReport.data, null, 2).slice(0, 2000)}
                </pre>
              </div>
            )}
          </div>
          )}

          {/* Blocking items (legacy) */}
          {showLegacy && readiness && readiness.blocking_items && readiness.blocking_items.length > 0 && (
            <div style={{ padding: 16, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca' }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: '#991b1b' }}>
                Blocking Items ({readiness.blocking_items.length})
              </div>
              {readiness.blocking_items.map((item: any, i: number) => (
                <div key={i} style={{ fontSize: 12, padding: '4px 0', borderBottom: i < readiness.blocking_items.length - 1 ? '1px solid #fecaca' : 'none' }}>
                  <span style={{ fontWeight: 600, color: item.severity === 'CRITICAL' ? '#991b1b' : '#92400e' }}>
                    [{item.severity || 'WARNING'}]
                  </span>{' '}
                  {item.description || item.message || JSON.stringify(item)}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB: Exceptions (M26B) ═══ */}
      {tab === 'Exceptions' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Exception Inbox <span style={{ fontSize: 11, color: 'var(--text-hint)', fontWeight: 400 }}>
              · auto-spawned by continuous-close engines when something needs human judgment
            </span>
          </div>
          {exceptions.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>✓ Inbox empty</div>
              <div style={{ fontSize: 12, color: '#14532d', marginTop: 6 }}>
                Continuous close handled everything. No human action required.
              </div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                {['Engine', 'Task', 'Department', 'Period', 'Entity', 'Created', 'Status'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {exceptions.map((x: any) => (
                  <tr key={x.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, color: '#3b82f6' }}>{x.source_engine}</td>
                    <td style={{ ...tdStyle, maxWidth: 380 }}>
                      <div style={{ fontWeight: 600 }}>{x.task_name}</div>
                      {x.notes && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>{x.notes}</div>}
                    </td>
                    <td style={tdStyle}>{x.department}</td>
                    <td style={tdStyle}>{x.period}</td>
                    <td style={tdStyle}>{x.entity_code || 'ALL'}</td>
                    <td style={{ ...tdStyle, fontSize: 11 }}>{x.created_at ? new Date(x.created_at).toLocaleString() : '-'}</td>
                    <td style={tdStyle}><StatusBadge status={x.status} size="sm" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 16, padding: 10, background: 'var(--bg-elevated)', borderRadius: 6, fontSize: 11, color: 'var(--text-hint)' }}>
            Exceptions are auto-resolved on the next continuous-close run when the underlying condition is gone.
            Engines that surface exceptions today: fx_revaluation, grni_aging, lease_amortization, cost_allocations.
          </div>
        </div>
      )}

      {/* ═══ TAB: Legacy Checklist (hidden, kept for admin/transition) ═══ */}
      {showLegacy && tab === 'Checklist' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <input type="month" value={checklistPeriod} onChange={e => { setChecklistPeriod(e.target.value); }}
              style={inputStyle} title="Checklist Period" aria-label="Checklist Period" placeholder="YYYY-MM" />
            <ActionButton label="Load" size="sm" onClick={loadChecklist} />
            {checklist && !checklist.exists && (
              <ActionButton label="Create Checklist" onClick={() => setShowCreateChecklist(true)} />
            )}
          </div>

          {checklist && !checklist.exists && (
            <div style={{ textAlign: 'center', color: 'var(--text-hint)', padding: 32 }}>
              No checklist for {checklistPeriod}. Click "Create Checklist" to instantiate from template.
            </div>
          )}

          {checklist && checklist.exists && (
            <>
              {/* Progress bar */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                  <span>{checklist.completed_tasks}/{checklist.total_tasks} tasks complete</span>
                  <span style={{ fontWeight: 600 }}>{checklist.progress_pct}%</span>
                </div>
                <div style={{ height: 8, background: 'var(--bg-elevated)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 4, width: `${checklist.progress_pct}%`,
                    background: checklist.progress_pct >= 100 ? '#16a34a' : '#1e3a5f' }} />
                </div>
              </div>

              {/* Tasks by department */}
              {Object.entries(checklist.tasks_by_department || {}).map(([dept, tasks]: [string, any]) => (
                <div key={dept} style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6, color: 'var(--text-secondary)' }}>{dept}</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead><tr>
                      {['#', 'Task', 'Status', 'Deadline', 'Sign-off', 'Actions'].map(h => (
                        <th key={h} style={thStyle}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {tasks.map((t: any) => (
                        <tr key={t.id} style={{ background: t.is_overdue ? '#fef2f2' : 'transparent' }}>
                          <td style={tdStyle}>{t.order_index + 1}</td>
                          <td style={{ ...tdStyle, maxWidth: 300 }}>
                            {t.task_name}
                            {t.dependencies?.length > 0 && (
                              <span style={{ fontSize: 10, color: 'var(--text-hint)', marginLeft: 6 }}>
                                (depends on step {t.dependencies.map((d: number) => d + 1).join(', ')})
                              </span>
                            )}
                          </td>
                          <td style={tdStyle}><StatusBadge status={t.status} size="sm" /></td>
                          <td style={{ ...tdStyle, color: t.is_overdue ? 'var(--negative)' : undefined, fontSize: 12 }}>
                            {t.deadline ? new Date(t.deadline).toLocaleDateString() : '-'}
                            {t.is_overdue && <span style={{ marginLeft: 4, fontWeight: 700 }}>OVERDUE</span>}
                          </td>
                          <td style={tdStyle}>
                            {t.sign_off_required ? (
                              t.signed_off_by ? (
                                <span style={{ color: 'var(--positive)', fontWeight: 600, fontSize: 12 }}>Signed off</span>
                              ) : (
                                <span style={{ color: 'var(--warning)', fontSize: 12 }}>Required</span>
                              )
                            ) : <span style={{ color: 'var(--text-hint)', fontSize: 12 }}>N/A</span>}
                          </td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {t.status === 'PENDING' && (
                                <ActionButton label="Start" size="sm" onClick={() => handleTaskAction(t.id, 'update', { status: 'IN_PROGRESS' })} />
                              )}
                              {t.status === 'IN_PROGRESS' && (
                                <ActionButton label="Complete" size="sm" variant="success" onClick={() => handleTaskAction(t.id, 'update', { status: 'COMPLETE' })} />
                              )}
                              {t.status === 'COMPLETE' && t.sign_off_required && !t.signed_off_by && (
                                <ActionButton label="Sign Off" size="sm" variant="success" onClick={() => handleTaskAction(t.id, 'sign-off', {})} />
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* ═══ TAB: Gantt (legacy, hidden) ═══ */}
      {showLegacy && tab === 'Gantt' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Close Task Timeline {checklist?.exists && checklist?.tasks ? `(${checklist.tasks.length} tasks)` : ''}
          </div>
          {!checklist?.exists && (
            <div style={{ textAlign: 'center', color: 'var(--text-hint)', padding: 32 }}>
              Load a checklist on the Checklist tab to see the Gantt view.
            </div>
          )}
          {checklist?.exists && checklist?.tasks && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Header row with day numbers */}
              <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 4 }}>
                <div style={{ width: 280, fontSize: 10, fontWeight: 700, color: 'var(--text-hint)' }}>TASK</div>
                <div style={{ flex: 1, display: 'flex', position: 'relative' }}>
                  {[-1, 1, 3, 5, 7, 9, 10].map(d => (
                    <div key={d} style={{ flex: 1, fontSize: 9, color: 'var(--text-hint)', textAlign: 'center' }}>D{d > 0 ? '+' : ''}{d}</div>
                  ))}
                </div>
                <div style={{ width: 80, fontSize: 10, fontWeight: 700, color: 'var(--text-hint)' }}>STATUS</div>
              </div>
              {(checklist.tasks as any[]).map((t: any, idx: number) => {
                const offset = t.notes_offset || 0;
                const colorByStatus = {
                  PENDING: '#94a3b8', IN_PROGRESS: '#3b82f6',
                  COMPLETE: t.signed_off_by ? '#16a34a' : '#22c55e',
                }[t.status as string] || '#94a3b8';
                // Bar position: deadline_offset is days from period end (-1 to +10)
                // We render across an 11-unit range
                let dlOffset = 5;
                if (t.deadline) {
                  const dl = new Date(t.deadline);
                  const periodMonth = parseInt(checklistPeriod.split('-')[1]) - 1;
                  const periodYear = parseInt(checklistPeriod.split('-')[0]);
                  const periodEnd = new Date(periodYear, periodMonth + 1, 0);
                  const raw = Math.round((dl.getTime() - periodEnd.getTime()) / 86400000);
                  dlOffset = Math.max(-1, Math.min(10, raw));
                }
                const startPct = ((dlOffset + 1) / 11) * 100 - 8;
                const widthPct = 8;
                const isCriticalPath = t.dependencies?.length > 1 || t.sign_off_required;
                return (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-subtle)' }}>
                    <div style={{ width: 280, fontSize: 11, paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isCriticalPath ? 600 : 400 }}>
                      <span style={{ display: 'inline-block', width: 16, color: 'var(--text-hint)' }}>{idx + 1}</span>
                      {isCriticalPath && <span style={{ color: '#f59e0b', marginRight: 4 }}>★</span>}
                      {t.task_name}
                    </div>
                    <div style={{ flex: 1, position: 'relative', height: 18, background: 'var(--bg-elevated)', borderRadius: 4 }}>
                      <div style={{
                        position: 'absolute', left: `${Math.max(0, startPct)}%`,
                        width: `${widthPct}%`, height: '100%', borderRadius: 4,
                        background: colorByStatus,
                        opacity: t.status === 'COMPLETE' ? 0.7 : 0.9,
                        border: isCriticalPath ? '1px solid #f59e0b' : 'none',
                      }} title={`${t.task_name} — ${t.status} — deadline ${t.deadline?.slice(0, 10) || 'n/a'}`} />
                      {/* Period end vertical line at D0 */}
                      <div style={{
                        position: 'absolute', left: `${(1 / 11) * 100}%`, top: 0, bottom: 0,
                        width: 1, background: '#dc2626',
                      }} />
                    </div>
                    <div style={{ width: 80, paddingLeft: 8 }}>
                      <StatusBadge status={t.status} size="sm" />
                    </div>
                  </div>
                );
              })}
              <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: 'var(--text-hint)' }}>
                <span><span style={{ color: '#f59e0b' }}>★</span> Critical path</span>
                <span><span style={{ width: 12, height: 4, background: '#22c55e', display: 'inline-block', marginRight: 4 }} /> Complete</span>
                <span><span style={{ width: 12, height: 4, background: '#3b82f6', display: 'inline-block', marginRight: 4 }} /> In progress</span>
                <span><span style={{ width: 12, height: 4, background: '#94a3b8', display: 'inline-block', marginRight: 4 }} /> Pending</span>
                <span><span style={{ width: 1, height: 12, background: '#dc2626', display: 'inline-block', marginRight: 4 }} /> Period end</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ═══ TAB: Period Lock (M26B simplified) ═══ */}
      {tab === 'Period Lock' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <div style={lbl}>Fiscal Year</div>
            <input type="number" value={pcYear} onChange={e => setPcYear(parseInt(e.target.value) || new Date().getFullYear())} style={{ ...inputStyle, width: 100 }} />
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['Entity', 'Period', 'Status', 'Soft Closed', 'Hard Closed', 'By', 'Post-Close Adj', 'Actions'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {periods.length === 0 ? (
                <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-hint)' }}>No period controls found.</td></tr>
              ) : periods.map(pc => (
                <tr key={pc.id}>
                  <td style={tdStyle}>{pc.entity_code}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{pc.period}</td>
                  <td style={tdStyle}><StatusBadge status={pc.period_status} size="sm" /></td>
                  <td style={{ ...tdStyle, fontSize: 11 }}>{pc.soft_closed_at ? new Date(pc.soft_closed_at).toLocaleString() : '-'}</td>
                  <td style={{ ...tdStyle, fontSize: 11 }}>{pc.hard_closed_at ? new Date(pc.hard_closed_at).toLocaleString() : '-'}</td>
                  <td style={tdStyle}>{pc.closed_by || '-'}</td>
                  <td style={{ ...tdStyle, color: pc.post_close_adjustments > 0 ? 'var(--warning)' : undefined, fontWeight: pc.post_close_adjustments > 0 ? 600 : 400 }}>
                    {pc.post_close_adjustments || 0}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {pc.period_status === 'OPEN' && (
                        <ActionButton label="Soft Close" size="sm" variant="secondary"
                          onClick={() => handlePeriodAction(pc.entity_code, pc.fiscal_year, pc.fiscal_month, 'soft-close')} />
                      )}
                      {(pc.period_status === 'OPEN' || pc.period_status === 'SOFT_CLOSED') && (
                        <ActionButton label="Hard Close" size="sm" variant="danger"
                          onClick={() => handlePeriodAction(pc.entity_code, pc.fiscal_year, pc.fiscal_month, 'hard-close')} />
                      )}
                      {pc.period_status === 'SOFT_CLOSED' && (
                        <ActionButton label="Reopen" size="sm"
                          onClick={() => handlePeriodAction(pc.entity_code, pc.fiscal_year, pc.fiscal_month, 'reopen')} />
                      )}
                      {pc.period_status === 'HARD_CLOSED' && (
                        <ActionButton label="CFO Reopen" size="sm" variant="danger"
                          onClick={() => { setCfoReopenTarget(pc); setCfoReopenReason(''); }} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ TAB: Audit Trail (M26B.4) ═══ */}
      {tab === 'Audit Trail' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Continuous-Close Audit Trail
            <span style={{ fontSize: 11, color: 'var(--text-hint)', fontWeight: 400, marginLeft: 8 }}>
              · every JE posted by an auto-close engine in {currentPeriod}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
            <div style={lbl}>Filter Source</div>
            <select
              value={auditSourceFilter}
              onChange={e => setAuditSourceFilter(e.target.value)}
              title="Filter by source type"
              aria-label="Filter audit trail by source type"
              style={{ ...inputStyle, minWidth: 220 }}
            >
              <option value="">(all engines)</option>
              {auditSourceTypes.map(st => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
            <ActionButton label="Refresh" size="sm" onClick={loadAuditTrail} />
            <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>
              {auditTrail.length} JE{auditTrail.length === 1 ? '' : 's'}
            </span>
          </div>
          {auditTrail.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-hint)' }}>
              No continuous-close JEs in {currentPeriod}{auditSourceFilter ? ` for source ${auditSourceFilter}` : ''}.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>
                {['JE #', 'Source Engine', 'Entity', 'Posted Date', 'Amount', 'Description', 'Posted By'].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {auditTrail.map((je: any) => (
                  <tr key={je.je_id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>
                      <a href={`/gl-workbench?je_id=${je.je_id}`} style={{ color: '#3b82f6', textDecoration: 'none' }}>
                        {je.je_number}
                      </a>
                    </td>
                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11, color: '#3b82f6' }}>{je.source_type}</td>
                    <td style={tdStyle}>{je.entity_code || '-'}</td>
                    <td style={tdStyle}>{je.posting_date}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, textAlign: 'right' }}>{formatAmount(je.total_amount)}</td>
                    <td style={{ ...tdStyle, fontSize: 11, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {je.description}
                    </td>
                    <td style={{ ...tdStyle, fontSize: 11 }}>{je.posted_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ═══ TAB: Closing Entries (legacy, hidden) ═══ */}
      {showLegacy && tab === 'Closing Entries' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
            <div style={lbl}>Period</div>
            <input type="month" value={closingPeriod} onChange={e => setClosingPeriod(e.target.value)} style={inputStyle} />
            <ActionButton label="Preview Closing JE" onClick={handlePreviewClosing} />
          </div>

          {/* History */}
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Closing Entry History</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr>
              {['JE #', 'Period', 'Entity', 'Posted Date', 'Posted By', 'Description'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {closingHistory.length === 0 ? (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-hint)' }}>No closing entries posted yet.</td></tr>
              ) : closingHistory.map(je => (
                <tr key={je.id}>
                  <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{je.je_number}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{je.period}</td>
                  <td style={tdStyle}>{je.entity_code || 'All'}</td>
                  <td style={tdStyle}>{je.posting_date}</td>
                  <td style={tdStyle}>{je.posted_by}</td>
                  <td style={{ ...tdStyle, fontSize: 12 }}>{je.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Create Checklist Modal ═══ */}
      <Modal open={showCreateChecklist} onClose={() => setShowCreateChecklist(false)} title={`Create Checklist — ${checklistPeriod}`} size="md">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 8 }}>
          <div style={{ fontSize: 13 }}>Select a template to instantiate tasks for period <strong>{checklistPeriod}</strong>:</div>
          {templates.length === 0 ? (
            <div style={{ color: 'var(--text-hint)' }}>No templates found.</div>
          ) : templates.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: 12, borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{t.template_name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>{t.task_count} tasks | {t.department}</div>
              </div>
              <ActionButton label="Use Template" size="sm" onClick={() => handleCreateChecklist(t.id)} />
            </div>
          ))}
          <ActionButton label="Use Default Template" variant="secondary" onClick={() => handleCreateChecklist()} />
        </div>
      </Modal>

      {/* ═══ Preview Closing JE Modal ═══ */}
      <Modal open={showPreview} onClose={() => setShowPreview(false)} title={`Closing Entry Preview — ${closingPeriod}`} size="lg"
        footer={<>
          <ActionButton label="Cancel" variant="secondary" onClick={() => setShowPreview(false)} />
          <ActionButton label="Post Closing JE" variant="danger" onClick={handlePostClosing} />
        </>}>
        {closingPreview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div style={{ padding: 12, borderRadius: 8, background: '#f0fdf4', textAlign: 'center' }}>
                <div style={lbl}>Total Revenue</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--positive)' }}>{formatAmount(closingPreview.total_revenue)}</div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: '#fef2f2', textAlign: 'center' }}>
                <div style={lbl}>Total Expense</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--negative)' }}>{formatAmount(closingPreview.total_expense)}</div>
              </div>
              <div style={{ padding: 12, borderRadius: 8, background: 'var(--accent-subtle)', textAlign: 'center' }}>
                <div style={lbl}>Net Income to RE</div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{formatAmount(closingPreview.net_income)}</div>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr>
                {['Account', 'Name', 'Type', 'Amount', 'Action'].map(h => (
                  <th key={h} style={{ ...thStyle, fontSize: 10 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {closingPreview.lines?.map((l: any, i: number) => (
                  <tr key={i}>
                    <td style={{ ...tdStyle, fontFamily: 'monospace' }}>{l.account_code}</td>
                    <td style={tdStyle}>{l.account_name}</td>
                    <td style={tdStyle}><StatusBadge status={l.type} size="sm" /></td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{formatAmount(l.amount)}</td>
                    <td style={{ ...tdStyle, fontSize: 11, color: 'var(--text-hint)' }}>
                      {l.type === 'REVENUE' ? 'DR (zero credit balance)' : 'CR (zero debit balance)'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ fontSize: 11, color: 'var(--text-hint)', padding: 8, borderTop: '1px solid var(--border-subtle)' }}>
              This will post {closingPreview.line_count} lines + 1 net income line to Retained Earnings.
              The closing JE zeros all P&L accounts for {closingPeriod}.
            </div>
          </div>
        )}
      </Modal>

      {/* ═══ CFO Override Reopen Modal (M24A.4) ═══ */}
      <Modal open={!!cfoReopenTarget} onClose={() => setCfoReopenTarget(null)}
        title={cfoReopenTarget ? `CFO Override Reopen — ${cfoReopenTarget.entity_code} / ${cfoReopenTarget.fiscal_year}-${String(cfoReopenTarget.fiscal_month).padStart(2, '0')}` : ''}
        size="md"
        footer={<>
          <ActionButton label="Cancel" variant="secondary" onClick={() => setCfoReopenTarget(null)} />
          <ActionButton label="Reopen HARD_CLOSED Period" variant="danger" onClick={handleCfoReopen} />
        </>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>
              SOX 404 / ICFR Override
            </div>
            <div style={{ fontSize: 12, color: '#7f1d1d' }}>
              Reopening a HARD_CLOSED period is a CFO-controlled act of last resort.
              The reason below is permanently recorded in the audit trail and surfaces
              in the IA report. Requires role CFO / ADMIN / SUPER_ADMIN.
            </div>
          </div>
          <div>
            <div style={lbl}>Reason for reopen (min 10 characters)</div>
            <textarea
              value={cfoReopenReason}
              onChange={e => setCfoReopenReason(e.target.value)}
              placeholder="e.g. Q1 audit adjustment per external auditor letter dated 2026-06-15..."
              style={{ width: '100%', minHeight: 100, padding: 10, borderRadius: 6,
                       border: '1px solid var(--border-subtle)', fontSize: 13, fontFamily: 'inherit' }}
            />
            <div style={{ fontSize: 10, color: cfoReopenReason.length < 10 ? 'var(--negative)' : 'var(--text-hint)', marginTop: 4 }}>
              {cfoReopenReason.length} / 10 characters minimum
            </div>
          </div>
        </div>
      </Modal>
    </PageLayout>
  );
}
